import React, { useContext, type FC } from "react";
import InvitationQRCode from "./InvitationQRCode";
import { InvitationAvatar, InvitationOwnerAvatar } from "./InvitationAvatars";
import { WEBSITE_URL } from "~/src/utils/common";
import { PartyInvitationCardContext, Skeleton } from "./context";
import AvacusLogoLink from "../AvacusLogoLink";
import { Body1, H1, H4 } from "../Common/Text";
import { useTranslations } from "next-intl";

const InvitationCard: FC<{ qrCodeURL: string; loadingQr?: boolean }> = ({
  qrCodeURL,
  loadingQr = false,
}) => {
  const t = useTranslations();

  const {
    name = "",
    ownerName = "",
    members = 0,
    isRendering = true,
  } = useContext(PartyInvitationCardContext);
  const rawText = t("invitation_to_join_party_chat");

  return (
    <div className="flex flex-col justify-center items-center rounded-xl gap-8">
      <p className="font-bold text-base md:text-[24px] md:print:text-[24px] leading-5 md:leading-9 tracking-normal text-center print:opacity-100!">
        {rawText}
      </p>
      <div className="flex justify-center items-end gap-12">
        <div className="flex justify-start flex-col gap-2">
          <AvacusLogoLink />
          <div className="flex justify-center items-center border border-primary rounded-3xl h-auto mx-auto max-w-[320px] p-6 bg-white">
            <InvitationQRCode value={qrCodeURL} loading={loadingQr} />
          </div>
        </div>

        <div className="flex w-100 shrink-0 flex-col text-subtle-light gap-6 justify-center items-center">
          <div className="size-37.5 shrink-0">
            <InvitationAvatar />
          </div>
          <div className="relative flex h-[46px] w-full min-w-0 flex-row items-center justify-center gap-4">
            <img
              src={`${WEBSITE_URL}/static/confetti-icon.svg`}
              alt="Confetti"
              loading="lazy"
              decoding="async"
              className="size-7 shrink-0 object-contain"
            />
            {isRendering ? (
              <Skeleton
                className="border border-primary"
                width={180}
                height={32}
                borderRadius={12}
              />
            ) : (
              <H1 className="min-w-0 truncate text-primary font-semibold">
                {name?.length > 16
                  ? name.substring(0, 15) + "..."
                  : name || t("party_chat")}
              </H1>
            )}
          </div>
          <div className="flex justify-between items-center w-full border border-primary rounded-2xl py-2.5 px-8 bg-white h-13">
            <H4 className="font-medium">{t("web.members")}:</H4>
            {isRendering ? <Skeleton width={48} /> : <H4>{members}</H4>}
          </div>
          <div className="flex justify-between items-center w-full border border-primary rounded-2xl py-2.5 px-8 bg-white h-13 gap-4">
            <H4 className="font-medium">{t("owner")}:</H4>
            {isRendering ? (
              <div className="flex justify-center items-center gap-2">
                <Skeleton width={32} height={32} borderRadius={32} />
                <Skeleton width={80} />
              </div>
            ) : (
              <div className="w-full flex flex-1 justify-end items-center overflow-hidden max-w-62.5">
                <div className="w-fit flex justify-center items-center">
                  <InvitationOwnerAvatar />
                </div>
                <H4 className="ml-2 break-all line-clamp-1">{ownerName}</H4>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="w-full border rounded-xl border-blue-info flex justify-center items-center bg-blue-2 p-2 gap-2">
        <img
          src={`${WEBSITE_URL}/static/ic-scan-blue.svg`}
          alt="Scan"
          loading="lazy"
          decoding="async"
          className="size-5 object-contain"
        />
        <Body1 className="text-blue-info text-center w-auto whitespace-nowrap">
          {t("web.scan_qr_code_on_avacus_app")}
        </Body1>
      </div>
    </div>
  );
};
export default InvitationCard;
